import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

// Basic selectors
export const selectStagesState = (state: RootState) => state.stages;
export const selectStages = (state: RootState) => state.stages.stages;
export const selectStagesLoading = (state: RootState) => state.stages.loading;
export const selectStagesError = (state: RootState) => state.stages.error;
export const selectNewCandidateCount = (state: RootState) => state.stages.newCandidateCount;
export const selectCandidateCounts = (state: RootState) => state.stages.candidateCounts;
export const selectLastFetched = (state: RootState) => state.stages.lastFetched;

// Memoized selectors
export const selectActiveStages = createSelector(
    [selectStages],
    (stages) => stages.filter(stage => stage.isActive)
);

export const selectStagesSortedByOrder = createSelector(
    [selectStages],
    (stages) => [...stages].sort((a, b) => {
        // First sort by active status (active stages first)
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;

        // Then sort by displayOrder within each status group
        return a.displayOrder - b.displayOrder;
    })
);

export const selectActiveStagesSortedByOrder = createSelector(
    [selectActiveStages],
    (activeStages) => [...activeStages].sort((a, b) => a.displayOrder - b.displayOrder)
);

export const selectStageById = createSelector(
    [selectStages, (_, stageId: string) => stageId],
    (stages, stageId) => stages.find(stage => stage.id === stageId)
);

export const selectStagesWithCandidateCounts = createSelector(
    [selectStages, selectCandidateCounts],
    (stages, candidateCounts) =>
        stages.map(stage => ({
            ...stage,
            candidateCount: candidateCounts[stage.id] || stage.candidateCount || 0
        }))
);

export const selectActiveStagesWithCandidateCounts = createSelector(
    [selectStagesWithCandidateCounts],
    (stagesWithCounts) =>
        stagesWithCounts
            .filter(stage => stage.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)
);

export const selectStagesRequiringEmail = createSelector(
    [selectStages],
    (stages) => stages.filter(stage => stage.requiresEmail)
);

export const selectAutoAdvancingStages = createSelector(
    [selectStages],
    (stages) => stages.filter(stage => stage.autoAdvanceAfterDays)
);

export const selectTotalCandidateCount = createSelector(
    [selectCandidateCounts],
    (candidateCounts) => Object.values(candidateCounts).reduce((sum, count) => sum + count, 0)
);

export const selectStageStats = createSelector(
    [selectActiveStagesWithCandidateCounts],
    (stages) => stages.map(stage => ({
        id: stage.id,
        name: stage.name,
        count: stage.candidateCount,
        displayOrder: stage.displayOrder
    }))
);

// Helper selector to check if data is stale (older than 5 minutes)
export const selectIsStagesDataStale = createSelector(
    [selectLastFetched],
    (lastFetched) => {
        if (!lastFetched) return true;
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return lastFetched < fiveMinutesAgo;
    }
);